import ClickAwayListener from '@mui/material/ClickAwayListener';
import Stack from '@mui/material/Stack';
import is from '@sindresorhus/is';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Dropdown } from './DropDown';
import { GlobalSearchInput } from './GlobalSearchInput';
import { useGlobalSearchTableParams } from './use-global-search-table-params';
import { useGlobalSearchQuery } from '../query-hooks/global-search/global-search';

import type { DropdownData } from './DropDown';
import type { TabParam } from './use-global-search-table-params';
import type { GlobalSearch } from '../../data-contract/global-search';
import type { FunctionComponent } from 'react';

const generateDropdownFlatList = (result: GlobalSearch | undefined): readonly DropdownData[] => {
    if (is.undefined(result)) {
        return [];
    }

    const { jobs, learningOpportunities, skills, users } = result;
    const dropDownSkills: readonly DropdownData[] =
        skills?.data.map((skill) => ({
            id: skill.id,
            type: 'skill',
        })) ?? [];
    const dropDownJobs: readonly DropdownData[] =
        jobs?.data.map((job) => ({
            id: job.id,
            type: 'job',
        })) ?? [];
    const dropDownUsers: readonly DropdownData[] =
        users?.data.map((user) => ({
            id: user.id,
            type: 'user',
        })) ?? [];
    const dropDownLearningOpportunities: readonly DropdownData[] =
        learningOpportunities?.data.map((learningOpportunity) => ({
            id: learningOpportunity.id,
            type: 'learning-opportunity',
        })) ?? [];

    return [...dropDownSkills, ...dropDownJobs, ...dropDownUsers, ...dropDownLearningOpportunities];
};

export const GlobalSearchBar: FunctionComponent = () => {
    const {
        redirectToSearchTerm,
        redirectToSkillIds,
        searchTermResult: searchTermInitialValue,
        skillIds: skillIdsInitialValue,
    } = useGlobalSearchTableParams();

    const dropdownPositionRef = useRef<HTMLDivElement>(null);

    const [open, setDropdownOpen] = useState<boolean>(false);
    const [dropdownSearchTerm, setDropdownSearchTerm] = useState(searchTermInitialValue);

    const [skillIds, setSkillIds] = useState<ReadonlyArray<string>>(skillIdsInitialValue);
    const [currentCursor, setCurrentCursor] = useState<number>(-1);

    const { data: result, isFetching } = useGlobalSearchQuery(dropdownSearchTerm, skillIds);

    const dropDownData = useMemo(() => generateDropdownFlatList(result), [result]);

    useEffect(() => {
        const updatedCursorIndex = is.nonEmptyArray(dropDownData) ? 0 : -1;
        setCurrentCursor(updatedCursorIndex);
    }, [dropDownData]);

    const onCloseDropdown = (): void => setDropdownOpen(false);
    const onOpenDropdown = (): void => setDropdownOpen(true);

    const getActiveDropdownElement = (): DropdownData | undefined => {
        if (currentCursor <= dropDownData.length - 1) {
            const active = dropDownData[currentCursor];
            if (!is.undefined(active)) {
                return active;
            }
        }

        return;
    };

    const handleGlobalSearch = (sectionTab?: TabParam): void => {
        if (is.nonEmptyArray(skillIds)) {
            redirectToSkillIds(skillIds, sectionTab);
        } else {
            redirectToSearchTerm(dropdownSearchTerm, sectionTab);
        }
        setDropdownSearchTerm('');
        onCloseDropdown();
    };

    const addSkillToSearch = (skillId: string): void => {
        setSkillIds([...skillIds, skillId]);
    };

    return (
        <ClickAwayListener onClickAway={onCloseDropdown}>
            <Stack
                ref={dropdownPositionRef}
                spacing={2}
                sx={{
                    display: 'flex',
                    margin: '0 10px',
                    maxWidth: '480px',
                    padding: '10px 0',
                    width: '100%',
                }}
            >
                <GlobalSearchInput
                    currentCursor={currentCursor}
                    dropDownData={dropDownData}
                    getActiveDropdownElement={getActiveDropdownElement}
                    handleGlobalSearch={handleGlobalSearch}
                    searchTerm={dropdownSearchTerm}
                    setCurrentCursor={setCurrentCursor}
                    setSearchTerm={setDropdownSearchTerm}
                    setSkillIds={setSkillIds}
                    skillIds={skillIds}
                    onCloseDropdown={onCloseDropdown}
                    onOpenDropdown={onOpenDropdown}
                />
                <Dropdown
                    activeId={getActiveDropdownElement()?.id ?? ''}
                    addSkillToSearch={addSkillToSearch}
                    handleGlobalSearch={handleGlobalSearch}
                    isFetching={isFetching}
                    open={open}
                    positionRef={dropdownPositionRef}
                    result={result}
                    searchTerm={dropdownSearchTerm}
                    setSearchTerm={setDropdownSearchTerm}
                    onCloseDropdown={onCloseDropdown}
                />
            </Stack>
        </ClickAwayListener>
    );
};
